import { Image, ImageProps } from "@/components/atoms/Image";
import { Text } from "@/components/atoms/Text";
import { cn } from "@/lib/utils";
import * as React from "react";

export type ListingSummaryImageProps = {
  src: string;
  alt: string;
  className?: string;
  imageProps?: Omit<ImageProps, "src" | "alt">;
  responsive?: boolean;
};

export const ListingSummaryImage = ({
  src,
  alt,
  className,
  imageProps,
  responsive = true,
}: ListingSummaryImageProps) => {
  return (
    <div
      className={cn(
        "tw-h-auto tw-min-w-full tw-mb-16 lg:tw-mb-16 lg:tw-h-auto lg:tw-w-full lg:tw-aspect-[352/196] tw-aspect-[352/196]",
        {
          "tw-h-[100px] tw-min-w-[100px] tw-mb-0 tw-aspect-auto": responsive,
        },
        className,
      )}
    >
      <Image
        loading="lazy"
        src={src}
        alt={alt}
        {...(imageProps || ({} as ImageProps))}
        className={cn(
          "tw-rounded-4 tw-object-cover tw-object-center tw-w-full lg:tw-w-full",
          { "tw-w-auto": responsive },
          imageProps?.className,
        )}
      />
    </div>
  );
};

export type ListingSummaryIconsProps = {
  children: React.ReactNode;
  className?: string;
};
export const ListingSummaryIcons = ({
  children,
  className,
}: ListingSummaryIconsProps) => {
  return (
    <div
      className={cn("tw-flex tw-items-center tw-flex-wrap tw-gap-8", className)}
    >
      {children}
    </div>
  );
};

export type ListingSummaryProps = {
  listing: {
    title: string;
    neighborhood?: string;
    state?: string;
    city: string;
    images: { url: string }[];
  };
  icons?: React.ReactNode;
  image?: React.ReactNode;
  className?: string;
  responsive?: boolean;
};
export const ListingSummary = ({
  listing,
  icons,
  image,
  className,
  responsive = true,
}: ListingSummaryProps) => {
  const getListingLocationDisplay = () => {
    if (listing.neighborhood && listing.city) {
      return `${listing.neighborhood}, ${listing.city}`;
    }

    return `${listing.city}, ${listing.state}`;
  };

  return (
    <div
      className={cn(
        "tw-gap-16 lg:tw-block",
        {
          "tw-flex": responsive,
        },
        className,
      )}
    >
      {image || (
        <ListingSummaryImage
          src={listing.images?.[0]?.url}
          alt={listing.title}
          responsive={responsive}
        />
      )}

      <div
        className={cn("tw-flex tw-flex-col tw-gap-8 lg:tw-gap-8", {
          "tw-gap-6": responsive,
        })}
      >
        <div
          className={cn("tw-flex tw-flex-col tw-gap-4 lg:tw-gap-4", {
            "tw-gap-6": responsive,
          })}
        >
          <Text typeface="headline-2-m">{listing.title}</Text>
          <Text className="tw-text-gray-600 tw-block">
            {getListingLocationDisplay()}
          </Text>
        </div>
        {icons}
      </div>
    </div>
  );
};
